import axios from 'axios'
import Cookies from 'universal-cookie'
import {
  setToken,
  sleep
} from 'core/helpers'
import store from 'state/store'
import { expireSessionAction } from 'state/actions/userActions'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { DEFAULT_SESSION_EXPIRED_MESSAGE } from './consts'
import { V1DealersClient } from 'generated/v1/v1DealersApi'
import { V1UsersClient } from '@v1UsersApi'
import { V1ReportsClient } from 'generated/v1/v1ReportsApi'
import { V1IntegrationsClient } from 'generated/v1/v1IntegrationsApi'
import { V1CasesClient } from 'generated/v1/v1CasesApi'
import { V1SharedClient } from 'generated/v1/v1SharedApi'
import { V1DealsClient } from '@v1DealsApi'

const cookies = new Cookies()

// -----------------------------------------------------------------//
// API for DCS
const retryPolicy = {
  max: parseInt(process.env.REACT_APP_RETRY_MAX as string, 10),
  count: 0,
  waitTimeInMs: 2000
}

let lastErrorStatusCode: number | undefined = undefined

export const HTTP = axios.create({
  // See config/*.env.js to edit base URL
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true,
  transformResponse: data => data
})

HTTP.interceptors.request.use(config => {
  config.headers = config.headers || {}
  config.headers['X-Client-Origin'] = window.location.href
  return config
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setupInterceptors = () => {
  const navigate = useNavigate()

  useEffect(() => {
    HTTP.interceptors.response.use((response) => {
      //console.log('response', response)
      lastErrorStatusCode = undefined
      retryPolicy.count = 0
      const token = cookies.get(process.env.REACT_APP_COOKIE_NAME as string)
      if (token)
        setToken(token) // update token expiration every successful request
      return response
    }, async (error) => {
      console.log('error', error)
      if (!error.response && retryPolicy.count < retryPolicy.max) {
        // network error has occurred likely because the api is down or overloaded
        await sleep(retryPolicy.waitTimeInMs)
        retryPolicy.count += 1
        return HTTP(error.config)
      }

      retryPolicy.count = 0
      if (error.response
        && error.response.status) {
        if (error.response.status === 401) {
          if (process.env.REACT_APP_LOGIN_REDIRECT!.startsWith('http')) {
            store.dispatch(expireSessionAction([DEFAULT_SESSION_EXPIRED_MESSAGE]) as never)
            window.location.href = process.env.REACT_APP_LOGIN_REDIRECT!
          }
          else {
            store.dispatch(expireSessionAction([DEFAULT_SESSION_EXPIRED_MESSAGE]) as never)
            setTimeout(() => navigate(process.env.REACT_APP_LOGIN_REDIRECT!), 1000)
          }
        }
        else if (error.response.status === 428 && lastErrorStatusCode !== error.response.status) {
          let message = ''
          try {
            const responseData = JSON.parse(error.response.data)
            if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
              message = responseData.errors[0]
            }
          } catch (parseError) { }
          const currentState = store.getState()

          const confirmUrl = currentState.app.locationHistory.length >= 0
            ? currentState.app.locationHistory[0]
            : undefined

          const cancelUrl = currentState.app.locationHistory.length >= 1
            ? currentState.app.locationHistory[1]
            : undefined

          navigate(process.env.REACT_APP_2FA_REDIRECT!, {
            state: {
              confirmUrl: confirmUrl?.pathname,
              cancelUrl: cancelUrl?.pathname,
              message: message,
            }
          })
        }

        lastErrorStatusCode = error.response.status
      }
      return Promise.reject(error)
    })
  }, [navigate])
}
export const V1ApiUsersClient = new V1UsersClient(undefined, HTTP)
export const V1ApiDealersClient = new V1DealersClient(undefined, HTTP)
export const V1ApiReportsClient = new V1ReportsClient(undefined, HTTP)
export const V1ApiIntegrationsClient = new V1IntegrationsClient(undefined, HTTP)
export const V1ApiCasesClient = new V1CasesClient(undefined, HTTP)
export const V1ApiSharedClient = new V1SharedClient(undefined, HTTP)
export const V1ApiDealsClient = new V1DealsClient(undefined, HTTP)

export const HTTP_OLD_SA = axios.create({
  // See config/*.env.js to edit base URL
  baseURL: process.env.REACT_APP_OLD_SYS_ADMIN_URL,
  withCredentials: true
})

// API for OLD SA
const oldSaRetryPolicy = {
  max: parseInt(process.env.REACT_APP_RETRY_MAX as string, 10),
  count: 0,
  waitTimeInMs: 2000
}

HTTP_OLD_SA.interceptors.response.use((response) => {
  oldSaRetryPolicy.count = 0
  return response
}, async (error) => {
  if (!error.response && oldSaRetryPolicy.count < oldSaRetryPolicy.max) {
    // network error has occurred likely because the api is down or overloaded
    await sleep(oldSaRetryPolicy.waitTimeInMs)
    oldSaRetryPolicy.count += 1
    return HTTP_OLD_SA(error.config)
  }

  oldSaRetryPolicy.count = 0
  return Promise.reject(error)
})